import { useState } from 'react';
import C from '../../constants/palette';
import StoneVisual from '../shared/StoneVisual';
import axios from 'axios';
import { displayImageUrl } from '../../utils/images';

const BLANK = {
  name: '', type: 'TREASURE', weight: '', origin: '',
  author: '', price: '', status: 'AVAILABLE', unheated: false, photo: '',
  sourceImageUrl: '', additionalImages: [], color: '#7c3aed', bg: '#2e1065',
};

function priceLimitFor(form) {
  return ['人民币', '纸币'].some(marker =>
    [form.name, form.author, form.type].filter(Boolean).some(value => String(value).includes(marker))
  ) ? 10000 : 29999;
}

export default function AdminEditPage({ stone, isNew, onSave, onBack }) {
  const [form, setForm]           = useState(isNew ? { ...BLANK } : { ...stone, price: String(stone.price) });
  const [saved, setSaved]         = useState(false);
  const [uploading, setUploading] = useState(false);
  const [additionalUploading, setAdditionalUploading] = useState(false);
  const [err, setErr]             = useState('');

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const priceLimit = priceLimitFor(form);
  const adminLine = (admin) => {
    if (!admin?.name && !admin?.email) return '—';
    return [admin.name, admin.email].filter(Boolean).join(' / ');
  };

  const fieldStyle = {
    width: '100%', padding: '11px 14px',
    border: `1px solid ${C.border}`, fontSize: 13,
    color: C.ink, outline: 'none', background: '#fff', fontFamily: 'DM Sans',
  };
  const labelStyle = {
    fontSize: 9, letterSpacing: '0.18em', color: C.muted,
    fontWeight: 600, display: 'block', marginBottom: 8,
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      if (isNew) {
        const id       = `JB-${Date.now().toString(36).toUpperCase().slice(-6)}`;
        const photo    = form.photo.trim();
        const payload  = { ...form, id, type: 'TREASURE', price: Math.min(parseInt(form.price, 10) || 0, priceLimit), photo, sourceImageUrl: '' };
        const res = await axios.post('/api/stones', payload);
        onSave(res.data);
      } else {
        const photo = form.photo.trim();
        const updated = { ...form, type: 'TREASURE', price: Math.min(parseInt(form.price, 10) || 0, priceLimit), photo, sourceImageUrl: '' };
        const res = await axios.put(`/api/stones/${stone.id}`, updated);
        onSave(res.data);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (error) {
      setErr(error.response?.data?.error || 'Save failed. Please try again.');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.append('image', file);
    if (!isNew) data.append('stoneId', stone.id);
    setUploading(true);
    setErr('');
    try {
      const res = await axios.post('/api/upload/image', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      f('photo', res.data.url);
      f('sourceImageUrl', '');
    } catch (error) {
      setErr(error.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleAdditionalUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || isNew) return;
    setAdditionalUploading(true);
    setErr('');
    try {
      const uploaded = [];
      for (const file of files) {
        const data = new FormData();
        data.append('image', file);
        data.append('stoneId', stone.id);
        data.append('additional', 'true');
        const res = await axios.post('/api/upload/image', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push(res.data.url);
      }
      setForm(current => ({ ...current, additionalImages: [...(current.additionalImages || []), ...uploaded] }));
    } catch (error) {
      setErr(error.response?.data?.error || 'Additional image upload failed. Please try again.');
    } finally {
      setAdditionalUploading(false);
    }
  };

  const removeAdditionalImage = (url) => {
    setForm(current => ({ ...current, additionalImages: (current.additionalImages || []).filter(image => image !== url) }));
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#F4F5F7', fontFamily: 'DM Sans' }}>

      {/* ── Sidebar ── */}
      <aside style={{ width: 220, flexShrink: 0, background: '#0F172A', display: 'flex', flexDirection: 'column', padding: '0 0 24px' }}>
        <div style={{ padding: '28px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ fontFamily: 'Playfair Display', fontSize: 16, color: '#fff', letterSpacing: '0.04em' }}>KINGSTRADE</p>
          <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.16em', marginTop: 2 }}>CONTROL CENTER</p>
        </div>
        <div style={{ flex: 1, padding: '20px 0' }}>
          <button onClick={onBack} style={{
            width: '100%', padding: '12px 24px',
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(212,175,55,0.12)', border: 'none',
            borderLeft: `2px solid ${C.gold}`, cursor: 'pointer',
            color: C.gold, fontSize: 12, letterSpacing: '0.1em', fontWeight: 500,
          }}>
            ← INVENTORY
          </button>
        </div>
      </aside>

      <main style={{ flex: 1, overflow: 'auto' }}>
        {/* Top bar */}
        <div style={{ height: 64, background: '#fff', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px' }}>
          <div>
            <p style={{ fontFamily: 'DM Mono', fontSize: 11, color: C.gold, marginBottom: 2 }}>
              {isNew ? 'NEW TREASURE' : stone.id}
            </p>
            <p style={{ fontSize: 14, color: C.ink, fontWeight: 500 }}>
              {isNew ? 'Add Treasure' : 'Edit Treasure'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={onBack} style={{ padding: '9px 20px', background: 'none', border: `1px solid ${C.border}`, cursor: 'pointer', fontSize: 11, letterSpacing: '0.1em', color: C.muted, borderRadius: 4 }}>
              DISCARD
            </button>
            <button onClick={handleSave} style={{
              padding: '9px 24px',
              background: saved ? '#2a9d5c' : C.gold,
              border: 'none', cursor: 'pointer',
              fontSize: 11, letterSpacing: '0.1em', fontWeight: 600, color: '#0F172A',
              borderRadius: 4, transition: 'background 0.3s',
            }}>
              {saved ? 'SAVED ✔' : isNew ? 'ADD TO INVENTORY' : 'SAVE CHANGES'}
            </button>
          </div>
        </div>

        <div style={{ padding: 40, maxWidth: 1000, display: 'grid', gridTemplateColumns: '300px 1fr', gap: 40 }}>

          {/* Left: preview + image upload */}
          <div>
            <div style={{ aspectRatio: '1/1', background: form.bg, overflow: 'hidden', marginBottom: 16 }}>
              <StoneVisual stone={form} size={300} />
            </div>
            <div style={{ background: '#fff', border: `1px solid ${C.border}`, padding: 20 }}>
              <p style={{ fontSize: 9, letterSpacing: '0.18em', color: C.muted, fontWeight: 600, marginBottom: 12 }}>PHOTO</p>
              {form.photo ? (
                <p style={{ fontSize: 11, fontFamily: 'DM Mono', color: C.ink, wordBreak: 'break-all', marginBottom: 12, lineHeight: 1.5 }}>
                  {form.photo}
                </p>
              ) : (
                <p style={{ fontSize: 11, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
                  No image uploaded yet.
                </p>
              )}
              <label style={{
                display: 'block', padding: '9px 0', textAlign: 'center',
                background: uploading ? '#e8d080' : C.gold,
                cursor: 'pointer', fontSize: 10, letterSpacing: '0.16em', fontWeight: 600, color: C.dark,
                marginBottom: 8,
              }}>
                {uploading ? 'UPLOADING…' : '↑ UPLOAD IMAGE FILE'}
                <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
              </label>
              {err && <p style={{ fontSize: 10, color: '#e03131', marginTop: 6 }}>{err}</p>}
              <p style={{ fontSize: 10, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
                Upload an image file. Images are served from the Kingstrade server. JPG / PNG / WebP / GIF up to 10 MB.
              </p>
            </div>
            <div style={{ background: '#fff', border: `1px solid ${C.border}`, padding: 20, marginTop: 16 }}>
              <p style={{ fontSize: 9, letterSpacing: '0.18em', color: C.muted, fontWeight: 600, marginBottom: 12 }}>ADDITIONAL IMAGES</p>
              {(form.additionalImages || []).length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
                  {(form.additionalImages || []).map(url => (
                    <div key={url} style={{ position: 'relative', aspectRatio: '1/1', background: form.bg, overflow: 'hidden', border: `1px solid ${C.border}` }}>
                      <img src={displayImageUrl(url)} alt="Additional gallery" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      <button type="button" onClick={() => removeAdditionalImage(url)} style={{ position: 'absolute', top: 4, right: 4, border: 'none', background: 'rgba(15,23,42,0.78)', color: '#fff', width: 22, height: 22, borderRadius: '50%', cursor: 'pointer', fontSize: 14, lineHeight: 1 }} aria-label="Remove additional image">×</button>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: 11, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>No additional images uploaded.</p>
              )}
              <label style={{ display: 'block', padding: '9px 0', textAlign: 'center', background: isNew || additionalUploading ? '#e8e9ec' : '#eef1f5', cursor: isNew || additionalUploading ? 'not-allowed' : 'pointer', fontSize: 10, letterSpacing: '0.12em', fontWeight: 600, color: isNew ? C.muted : C.ink }}>
                {additionalUploading ? 'UPLOADING…' : isNew ? 'SAVE TREASURE FIRST' : '↑ UPLOAD ADDITIONAL IMAGES'}
                <input type="file" accept="image/*" multiple disabled={isNew || additionalUploading} onChange={handleAdditionalUpload} style={{ display: 'none' }} />
              </label>
              <p style={{ fontSize: 10, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>Upload one or more gallery images. Changes to removed images are applied when you save.</p>
            </div>
          </div>

          {/* Right: form */}
          <form onSubmit={handleSave}>
            <div style={{ background: '#fff', border: `1px solid ${C.border}`, padding: 32, marginBottom: 24 }}>
              <p style={{ fontSize: 10, letterSpacing: '0.2em', color: C.ink, fontWeight: 600, marginBottom: 24 }}>TREASURE DETAILS</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={labelStyle}>TREASURE NAME</label>
                  <input value={form.name} onChange={e => f('name', e.target.value)} style={fieldStyle} required />
                </div>
                <div>
                  <label style={labelStyle}>AUTHOR / INSTITUTION</label>
                  <input value={form.author || ''} onChange={e => f('author', e.target.value)} placeholder="e.g. 张 烨" style={fieldStyle} />
                </div>
                <div>
                  <label style={labelStyle}>VALUATION</label>
                  <input type="number" min="0" max={priceLimit} value={form.price} onChange={e => f('price', e.target.value)} placeholder={priceLimit === 10000 ? 'e.g. 8800' : 'e.g. 24000'} style={{ ...fieldStyle, fontFamily: 'DM Mono' }} />
                  <p style={{ fontSize: 10, color: C.muted, marginTop: 7 }}>
                    Maximum valuation: ${priceLimit.toLocaleString()}
                  </p>
                </div>
                <div>
                  <label style={labelStyle}>STATUS</label>
                  <select value={form.status} onChange={e => f('status', e.target.value)} style={fieldStyle}>
                    {['AVAILABLE', 'SOLD'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Record info */}
            <div style={{ background: '#fff', border: `1px solid ${C.border}`, padding: 32 }}>
              <p style={{ fontSize: 10, letterSpacing: '0.2em', color: C.ink, fontWeight: 600, marginBottom: 20 }}>RECORD INFO</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {[
                  ['Treasure ID',   isNew ? '— assigned on save' : stone.id],
                  ['Date Added',    isNew ? '—' : new Date(stone.createdAt || Date.now()).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })],
                  ['Last Modified', isNew ? '—' : new Date(stone.updatedAt || Date.now()).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })],
                  ['Created By',    isNew ? '—' : adminLine(stone.createdByAdmin)],
                  ['Updated By',    isNew ? '—' : adminLine(stone.updatedByAdmin)],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p style={{ fontSize: 9, letterSpacing: '0.14em', color: C.muted, fontWeight: 600, marginBottom: 4 }}>{k}</p>
                    <p style={{ fontFamily: 'DM Mono', fontSize: 12, color: C.ink }}>{v}</p>
                  </div>
                ))}
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
